import { Button, Dropdown, Modal, Row, Col, Form } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useReport from '../../../../pages/user/support/hooks/useReport';
import { useToggle, useUser } from '../../../../hooks';
import { useForm } from 'react-hook-form';
import { ReportNew } from '../../../../types/support/supportReport';
import Select, { OnChangeValue } from 'react-select';
import { FormSelectionType } from '../../../../types/platform';
import { FormInput } from '../../../old';
import { toast } from 'react-toastify';

interface ReportButtonProps {
    entityType: string;
    entityId: string;
    entityCommentType: string | null;
}

export const ReportButton = ({ entityId, entityType, entityCommentType }: ReportButtonProps) => {
    const { t } = useTranslation();

    const [isOpen, toggleModal] = useToggle();
    const { onReportSubmit, reportCreatedSuccess, reportedObjectId } = useReport();
    const [reasonSelection, setReasonSelection] = useState<FormSelectionType | null>(null);

    useEffect(() => {
        if (reportCreatedSuccess && entityId === reportedObjectId) {
            toast.success(t('support.report_sent_succesfully'), {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: 'colored',
                icon: '✔',
            });
        }
    }, [reportCreatedSuccess]);

    const {
        register,
        handleSubmit,
        formState: { errors: formErrors },
        setValue,
        setError,
        clearErrors,
        control,
    } = useForm<ReportNew>();

    const processSubmit = async (data: ReportNew) => {
        if (data.description === undefined || data.description === null || data.description.length === 0) {
            setError('description', {});
        }
        if (data.reason === undefined || data.reason === null) {
            setError('reason', {});
        }

        if (data.reason === undefined || data.reason === null || data.description === undefined || data.description === null || data.description.length === 0) return;
        switch (entityType) {
            case 'LectureReport': {
                data.lectureId = entityId;
                break;
            }
            case 'ProblemReport': {
                data.problemId = entityId;
                break;
            }
            case 'ModuleReport': {
                data.moduleId = entityId;
                break;
            }
            case 'CommentReport': {
                data.commentId = entityId;
                data.commentType = entityCommentType;
                break;
            }
            case 'ChapterReport': {
                data.chapterId = entityId;
                break;
            }
            case 'MessageReport': {
                data.messageId = entityId;
                break;
            }
            case 'UserReport': {
                data.reportedUserId = entityId;
                break;
            }
            default: {
                break;
            }
        }
        data.type = entityType;
        data.typeApi = entityType.replace('Report', '').toLowerCase();
        toggleModal();
        onReportSubmit(data);
    };

    const reasonOptions: Record<string, Array<FormSelectionType>> = {
        ChapterReport: [
            {
                id: 0,
                value: '0',
                label: t('support.report_reason_chapter_0'),
            },
            {
                id: 1,
                value: '1',
                label: t('support.report_reason_chapter_1'),
            },
            {
                id: 2,
                value: '2',
                label: t('support.report_reason_chapter_2'),
            },
            {
                id: 3,
                value: '4',
                label: t('support.report_reason_default'),
            },
        ],
        ProblemReport: [
            {
                id: 0,
                value: '0',
                label: t('support.report_reason_problem_0'),
            },
            {
                id: 1,
                value: '1',
                label: t('support.report_reason_problem_1'),
            },
            {
                id: 2,
                value: '2',
                label: t('support.report_reason_problem_2'),
            },
            {
                id: 3,
                value: '3',
                label: t('support.report_reason_default'),
            },
        ],
        CommentReport: [
            {
                id: 0,
                value: '0',
                label: t('support.report_reason_comment_0'),
            },
            {
                id: 1,
                value: '1',
                label: t('support.report_reason_comment_1'),
            },
            {
                id: 2,
                value: '2',
                label: t('support.report_reason_default'),
            },
        ],
        ModuleReport: [
            {
                id: 0,
                value: '0',
                label: t('support.report_reason_module_0'),
            },
            {
                id: 1,
                value: '1',
                label: t('support.report_reason_module_1'),
            },
            {
                id: 2,
                value: '2',
                label: t('support.report_reason_default'),
            },
        ],
        LectureReport: [
            {
                id: 0,
                value: '0',
                label: t('support.report_reason_lecture_0'),
            },
            {
                id: 1,
                value: '1',
                label: t('support.report_reason_lecture_1'),
            },
            {
                id: 2,
                value: '2',
                label: t('support.report_reason_default'),
            },
        ],
        MessageReport: [
            {
                id: 0,
                value: '0',
                label: t('support.report_reason_message_0'),
            },
            {
                id: 1,
                value: '1',
                label: t('support.report_reason_message_1'),
            },
            {
                id: 2,
                value: '2',
                label: t('support.report_reason_default'),
            },
        ],
        UserReport: [
            {
                id: 0,
                value: '0',
                label: t('support.report_reason_user_0'),
            },
            {
                id: 1,
                value: '1',
                label: t('support.report_reason_user_1'),
            },
            {
                id: 2,
                value: '2',
                label: t('support.report_reason_user_2'),
            },
            {
                id: 3,
                value: '2',
                label: t('support.report_reason_default'),
            },
        ],
        // Add more entries here
    };

    const setFormReason = (option: OnChangeValue<FormSelectionType, false>) => {
        const typedOption = option as FormSelectionType;
        clearErrors('reason');
        setReasonSelection(option);
        setValue('reason', typedOption.label);
    };

    return (
        <>
            <Modal show={isOpen} onHide={toggleModal}>
                <Modal.Header className='bg-danger' onHide={toggleModal} closeButton>
                    <h5 className='modal-title' id='reportModel'>
                        {t('support.report_modal_title', { entity: t(`support.reports_table_entity_${entityType}`) })}
                    </h5>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit(processSubmit)}>
                        <Row>
                            <Col lg={12}>
                                <FormInput
                                    type='text'
                                    label={t('support.report_form_entity')}
                                    name='entity'
                                    className='form-control'
                                    containerClass={'mb-2'}
                                    defaultValue={t(`support.reports_table_entity_${entityType}`)}
                                    disabled={true}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={12}>
                                <Form.Group>
                                    <Form.Label>{t('support.report_form_reason_ph')}</Form.Label>
                                </Form.Group>
                                <Select
                                    className='react-select'
                                    classNamePrefix='react-select'
                                    options={reasonOptions[entityType]}
                                    onChange={(option) => setFormReason(option)}
                                    placeholder={t('support.report_form_reason_ph')}
                                    value={reasonSelection}
                                />
                                {formErrors.reason && <span className='text-danger'>{t('support.report_form_reason_req')}</span>}
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={12} className={'mt-2'}>
                                <FormInput
                                    type='textarea'
                                    label={t('support.report_form_description')}
                                    name='description'
                                    className='form-control'
                                    placeholder={t('support.report_form_description_ph')}
                                    containerClass={'mb-2'}
                                    register={register}
                                    errors={formErrors}
                                    control={control}
                                    style={{ height: '150px' }}
                                />
                                {formErrors.description && <span className='text-danger'>{t('support.report_form_description_req')}</span>}
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={12}>
                                <Button variant='success' type='submit'>
                                    {t('support.report_form_button')}
                                </Button>
                            </Col>
                        </Row>
                    </form>
                </Modal.Body>
            </Modal>
            <Dropdown>
                <Dropdown.Toggle variant='link' className='arrow-none card-drop p-0'>
                    <i className='text-danger mdi mdi-alert-circle'></i>
                </Dropdown.Toggle>
                <Dropdown.Menu className='bg-danger'>
                    <Dropdown.Item className='p-0 m-0'>
                        <Button
                            variant='danger'
                            className={'btn-block mt-0 mb-0 pt-0 pb-0'}
                            onClick={() => {
                                toggleModal();
                            }}>
                            {t('support.report_form_button')} {t(`support.reports_table_entity_${entityType}`).toLowerCase()}
                        </Button>
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        </>
    );
};
